/**
 * An Intake step's sources in the editor (AMO-169): the library, a space's
 * files, SharePoint, Google Drive and email. Each document source reads the
 * documents picked in it, a search, or both. Library and space documents are
 * picked from their lists; SharePoint and Drive are browsed through the
 * server (`browseIntakeStore`), inside the places an admin allowed. An email
 * source searches the mailbox of the person a run acts for, narrowed by
 * sender, subject or label, with that person's permission (`MailboxAccess`).
 *
 * @module components/workflows/IntakeSources
 */
import {
  WORKFLOW_INTAKE_SOURCE_KINDS,
  WORKFLOW_LIMITS,
  WORKFLOW_MAIL_APPS,
  type WorkflowIntakeMailFilter,
  type WorkflowIntakePick,
  type WorkflowIntakeSource,
  type WorkflowIntakeSourceKind,
  type WorkflowNode,
} from "@amoreg/contracts";
import { ChevronLeftIcon, FileTextIcon, FolderIcon, PlusIcon, XIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { usePolicies } from "~/state/policies";
import { useSpaceDocuments } from "~/state/spaces";
import { browseIntakeStore, type WorkflowWrite } from "~/state/workflows";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger } from "../ui/select";
import { Switch } from "../ui/switch";
import { SpaceChoice } from "./DestinationParts";
import { MAIL_APP_NAME, MailboxPermission } from "./MailboxAccess";
import { addIntakePick, INTAKE_SOURCE_LABEL, MAIL_DAYS, newIntakeSource } from "./workflowCanvas.logic";

type IntakeNode = Extract<WorkflowNode, { type: "intake" }>;

const SOURCE_TITLE: Readonly<Record<WorkflowIntakeSourceKind, string>> = {
  library: "Library",
  space: "Space files",
  sharepoint: "SharePoint",
  googledrive: "Google Drive",
  email: "Email",
};

const SEARCH_PLACEHOLDER: Readonly<Record<WorkflowIntakeSourceKind, string>> = {
  library: "e.g. client due diligence for {{input}}",
  space: "Words to look for, e.g. renewal",
  sharepoint: "e.g. board pack {{input}}",
  googledrive: "e.g. {{input}} engagement letter",
  email: "More words, e.g. {{input}}",
};

/** A list to pick from, filtered as the person types. */
function PickList({
  items,
  picked,
  onPick,
  empty,
}: {
  readonly items: ReadonlyArray<{ readonly id: string; readonly name: string }>;
  readonly picked: ReadonlySet<string>;
  readonly onPick: (item: { readonly id: string; readonly name: string }) => void;
  readonly empty: string;
}) {
  const [filter, setFilter] = useState("");
  const shown = items.filter((item) => !picked.has(item.id) && item.name.toLowerCase().includes(filter.trim().toLowerCase())).slice(0, 50);
  return (
    <div className="space-y-1.5" data-workflow-intake-picker="">
      <Input value={filter} placeholder="Filter" onChange={(event) => setFilter(event.currentTarget.value)} className="text-xs" />
      {shown.length === 0 ? (
        <p className="text-[11px] text-muted-foreground">{empty}</p>
      ) : (
        <ul className="max-h-48 space-y-0.5 overflow-y-auto">
          {shown.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                className="flex w-full items-center gap-1.5 rounded px-1.5 py-1 text-left text-[12px] hover:bg-muted"
                onClick={() => onPick(item)}
                data-workflow-intake-option={item.id}
              >
                <PlusIcon className="size-3 shrink-0 text-muted-foreground" />
                <span className="truncate">{item.name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function LibraryPicker({ organizationId, picked, onPick }: PickerProps) {
  const { snapshot, isPending } = usePolicies(organizationId);
  const items = useMemo(
    () => (snapshot?.policies ?? []).map((policy) => ({ id: policy.id, name: policy.title ?? policy.filename })),
    [snapshot],
  );
  return <PickList items={items} picked={picked} onPick={(item) => onPick(item)} empty={isPending ? "Loading the library…" : "No more documents."} />;
}

function SpacePicker({ spaceId, picked, onPick }: PickerProps & { readonly spaceId: string }) {
  const { items, isPending } = useSpaceDocuments(spaceId || null);
  const list = useMemo(() => items.map((document) => ({ id: document.docId, name: document.title ?? document.docId })), [items]);
  return <PickList items={list} picked={picked} onPick={(item) => onPick(item)} empty={isPending ? "Loading the space…" : "No more documents."} />;
}

interface PickerProps {
  readonly organizationId: string | null;
  readonly picked: ReadonlySet<string>;
  readonly onPick: (pick: WorkflowIntakePick) => void;
}

interface BrowseItem {
  readonly id: string;
  readonly name: string;
  readonly isFolder: boolean;
  readonly path?: string | undefined;
  readonly isLocation?: boolean | undefined;
}

/** One place being browsed: a SharePoint location and folder path, or a Drive folder. */
interface Place {
  readonly name: string;
  readonly location: string;
  readonly folder: string;
}

/** Browses SharePoint or Drive inside the allowed places; files and folders can be picked, folders opened. */
function StoreBrowser({
  organizationId,
  kind,
  picked,
  onPick,
  onLocation,
}: PickerProps & { readonly kind: "sharepoint" | "googledrive"; readonly onLocation: (location: string) => void }) {
  const [trail, setTrail] = useState<ReadonlyArray<Place>>([]);
  const here = trail.at(-1) ?? null;
  // The answer is kept with the place it is for, so a newer place shows Loading until its own answer arrives.
  const placeKey = `${kind} ${here?.location ?? ""} ${here?.folder ?? ""}`;
  const [loaded, setLoaded] = useState<{ readonly key: string; readonly result: WorkflowWrite<ReadonlyArray<BrowseItem>> } | null>(null);
  const answer = loaded?.key === placeKey ? loaded.result : null;
  useEffect(() => {
    if (!organizationId) return;
    let live = true;
    void browseIntakeStore(organizationId, { kind, ...(here ? { location: here.location, folder: here.folder } : {}) }).then((result) => {
      if (live) setLoaded({ key: placeKey, result });
    });
    return () => {
      live = false;
    };
  }, [organizationId, kind, here, placeKey]);
  const open = (item: { readonly id: string; readonly name: string; readonly path?: string | undefined; readonly isLocation?: boolean | undefined }) => {
    // A SharePoint location is a site to work in; a Drive location is a folder like any other.
    if (kind === "sharepoint" && item.isLocation) {
      onLocation(item.id);
      setTrail([{ name: item.name, location: item.id, folder: "" }]);
      return;
    }
    setTrail([...trail, kind === "sharepoint" ? { name: item.name, location: here?.location ?? "", folder: item.path ?? "" } : { name: item.name, location: "", folder: item.id }]);
  };
  return (
    <div className="space-y-1.5" data-workflow-intake-browser={kind}>
      <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
        {trail.length > 0 ? (
          <Button size="icon-xs" variant="ghost" aria-label="Back" onClick={() => setTrail(trail.slice(0, -1))}>
            <ChevronLeftIcon />
          </Button>
        ) : null}
        <span className="truncate">{here ? trail.map((place) => place.name).join(" / ") : "Allowed places"}</span>
      </div>
      {answer === null ? (
        <p className="text-[11px] text-muted-foreground">Loading…</p>
      ) : !answer.ok ? (
        <p className="text-[11px] text-destructive-foreground" data-workflow-intake-browse-error="">
          {answer.message}
        </p>
      ) : answer.value.length === 0 ? (
        <p className="text-[11px] text-muted-foreground">Nothing here.</p>
      ) : (
        <ul className="max-h-56 space-y-0.5 overflow-y-auto">
          {answer.value.map((item) => (
            <li key={item.id} className="flex items-center gap-1 rounded px-1 py-0.5 text-[12px] hover:bg-muted/60">
              {item.isFolder ? <FolderIcon className="size-3.5 shrink-0 text-muted-foreground" /> : <FileTextIcon className="size-3.5 shrink-0 text-muted-foreground" />}
              {item.isFolder ? (
                <button type="button" className="min-w-0 flex-1 truncate text-left hover:underline" onClick={() => open(item)} data-workflow-intake-open={item.id}>
                  {item.name}
                </button>
              ) : (
                <span className="min-w-0 flex-1 truncate">{item.name}</span>
              )}
              {item.isLocation || picked.has(item.id) ? null : (
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={() =>
                    onPick({
                      id: item.id,
                      name: item.name,
                      ...(item.isFolder ? { isFolder: true } : {}),
                      ...(item.isFolder && item.path ? { path: item.path } : {}),
                    })
                  }
                  data-workflow-intake-option={item.id}
                >
                  <PlusIcon />
                  {item.isFolder ? "Folder" : "Add"}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** An email source: which mailbox, what narrows its search, and the viewer's own permission. */
function EmailSource({
  source,
  organizationId,
  workflowId,
  onChange,
}: {
  readonly source: WorkflowIntakeSource;
  readonly organizationId: string | null;
  readonly workflowId: string | null;
  readonly onChange: (next: WorkflowIntakeSource) => void;
}) {
  const app = WORKFLOW_MAIL_APPS.find((entry) => entry === source.location) ?? "gmail";
  const mail: WorkflowIntakeMailFilter = source.mail ?? { from: "", subject: "", label: "", days: MAIL_DAYS.default };
  const setMail = (next: Partial<WorkflowIntakeMailFilter>) => onChange({ ...source, mail: { ...mail, ...next } });
  const narrow = mail.from.trim() !== "" || mail.subject.trim() !== "" || mail.label.trim() !== "";
  const field = (label: string, value: string, key: "from" | "subject" | "label", placeholder: string, maxLength: number) => (
    <label className="block space-y-1">
      <span className="text-[11px] font-medium">{label}</span>
      <Input
        value={value}
        maxLength={maxLength}
        placeholder={placeholder}
        onChange={(event) => setMail({ [key]: event.currentTarget.value })}
        className="text-xs"
        data-workflow-intake-mail={key}
      />
    </label>
  );
  return (
    <div className="space-y-2">
      <Select value={app} onValueChange={(value) => onChange({ ...source, location: value === "outlook" ? "outlook" : "gmail" })}>
        <SelectTrigger size="sm" aria-label="Mailbox" data-workflow-intake-mail-app="">
          <span className="text-xs">{MAIL_APP_NAME[app]}</span>
        </SelectTrigger>
        <SelectContent>
          {WORKFLOW_MAIL_APPS.map((entry) => (
            <SelectItem key={entry} value={entry}>
              {MAIL_APP_NAME[entry]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {field("From", mail.from, "from", "e.g. broker@example.com", 200)}
      {field("Subject", mail.subject, "subject", "e.g. renewal", 200)}
      {field(app === "outlook" ? "Folder" : "Label", mail.label, "label", app === "outlook" ? "e.g. Claims or Inbox/Claims" : "e.g. Clients", 100)}
      {narrow ? null : (
        <p className="rounded bg-warning/10 px-2 py-1 text-[11px] text-warning-foreground" data-workflow-intake-mail-broad="">
          Narrow the search by sender, subject or {app === "outlook" ? "folder" : "label"}: a workflow never searches a whole mailbox.
        </p>
      )}
      <label className="flex items-center gap-2 text-[11px]">
        <span className="font-medium">Mail from the last</span>
        <Input
          type="number"
          min={1}
          max={MAIL_DAYS.max}
          value={mail.days}
          onChange={(event) => {
            const days = Math.round(Number(event.currentTarget.value));
            if (Number.isFinite(days)) setMail({ days: Math.min(MAIL_DAYS.max, Math.max(1, days)) });
          }}
          className="w-16 text-xs"
          data-workflow-intake-mail-days=""
        />
        <span>days (at most {MAIL_DAYS.max})</span>
      </label>
      <label className="flex items-center gap-2 text-[11px]">
        <Switch
          checked={mail.attachments === true}
          onCheckedChange={(attachments) => setMail({ attachments, attachmentName: mail.attachmentName ?? "" })}
          data-workflow-intake-mail-attachments=""
        />
        <span className="font-medium">Read attached files too</span>
      </label>
      {mail.attachments ? (
        <label className="block space-y-1">
          <span className="text-[11px] font-medium">Only files named</span>
          <Input
            value={mail.attachmentName ?? ""}
            maxLength={100}
            placeholder="e.g. .xlsx or trades (optional)"
            onChange={(event) => setMail({ attachmentName: event.currentTarget.value })}
            className="text-xs"
            data-workflow-intake-mail-attachment-name=""
          />
          <span className="block text-[11px] leading-snug text-muted-foreground">
            PDF, Word, Excel, CSV and text files, up to 10 per search and 5 MB each; spreadsheets pass on as tables. Pictures
            are passed by. Like the email text, what they hold is removed from the run 14 days after it was read.
          </span>
        </label>
      ) : null}
      <MailboxPermission organizationId={organizationId} workflowId={workflowId} app={app} />
    </div>
  );
}

function SourceCard({
  source,
  index,
  organizationId,
  workflowId,
  onChange,
  onRemove,
}: {
  readonly source: WorkflowIntakeSource;
  readonly index: number;
  readonly organizationId: string | null;
  readonly workflowId: string | null;
  readonly onChange: (next: WorkflowIntakeSource) => void;
  readonly onRemove: () => void;
}) {
  const [picking, setPicking] = useState(false);
  const picked = new Set(source.picks.map((pick) => pick.id));
  const full = source.picks.length >= WORKFLOW_LIMITS.intakePicks;
  const pick = (entry: WorkflowIntakePick) => onChange(addIntakePick(source, entry));
  return (
    <li className="space-y-2 rounded-md bg-muted/40 p-2" data-workflow-intake-source={index}>
      <div className="flex items-center gap-2">
        <span className="flex-1 text-xs font-medium">{SOURCE_TITLE[source.kind]}</span>
        <Button size="icon-xs" variant="ghost" aria-label={`Remove ${SOURCE_TITLE[source.kind]}`} onClick={onRemove}>
          <XIcon />
        </Button>
      </div>
      {source.kind === "space" ? (
        <SpaceChoice
          organizationId={organizationId}
          spaceId={source.location}
          onChange={(location) => onChange({ ...source, location, picks: [] })}
          hint="Read as the person the run acts for, who must be on the space's team."
          name="intake-space"
        />
      ) : null}
      {source.kind === "email" ? <EmailSource source={source} organizationId={organizationId} workflowId={workflowId} onChange={onChange} /> : null}
      {source.kind === "sharepoint" && source.location ? (
        <p className="truncate text-[11px] text-muted-foreground" title={source.location}>
          Site: {source.location}
        </p>
      ) : null}
      {source.picks.length > 0 ? (
        <ul className="space-y-1">
          {source.picks.map((entry) => (
            <li key={entry.id} className="flex items-center gap-1.5 text-[12px]" data-workflow-intake-pick={entry.id}>
              {entry.isFolder ? <FolderIcon className="size-3.5 shrink-0 text-muted-foreground" /> : <FileTextIcon className="size-3.5 shrink-0 text-muted-foreground" />}
              <span className="min-w-0 flex-1 truncate">{entry.name}</span>
              <Button
                size="icon-xs"
                variant="ghost"
                aria-label={`Remove ${entry.name}`}
                onClick={() => onChange({ ...source, picks: source.picks.filter((other) => other.id !== entry.id) })}
              >
                <XIcon />
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
      {source.kind === "email" ? null : (
        <Button
          size="xs"
          variant="outline"
          disabled={full || (source.kind === "space" && !source.location)}
          onClick={() => setPicking((open) => !open)}
          data-workflow-intake-pick-toggle=""
        >
          {picking ? "Done picking" : "Pick documents"}
        </Button>
      )}
      {picking && !full && source.kind !== "email" ? (
        source.kind === "library" ? (
          <LibraryPicker organizationId={organizationId} picked={picked} onPick={pick} />
        ) : source.kind === "space" ? (
          <SpacePicker organizationId={organizationId} spaceId={source.location} picked={picked} onPick={pick} />
        ) : (
          <StoreBrowser
            organizationId={organizationId}
            kind={source.kind}
            picked={picked}
            onPick={pick}
            onLocation={(location) => onChange({ ...source, location })}
          />
        )
      ) : null}
      <label className="block space-y-1">
        <span className="text-[11px] font-medium">Search (optional)</span>
        <Input
          value={source.query}
          maxLength={500}
          placeholder={SEARCH_PLACEHOLDER[source.kind]}
          onChange={(event) => onChange({ ...source, query: event.currentTarget.value })}
          className="text-xs"
          data-workflow-intake-query=""
        />
      </label>
    </li>
  );
}

export function IntakeStepEditor({
  node,
  organizationId,
  workflowId,
  spaceId,
  onChange,
}: {
  readonly node: IntakeNode;
  readonly organizationId: string | null;
  /** The workflow being edited, for the viewer's own mailbox permission. */
  readonly workflowId: string | null;
  /** The workflow's own space, where a new Space files source starts. */
  readonly spaceId: string | null;
  readonly onChange: (next: IntakeNode) => void;
}) {
  const { sources } = node.config;
  const setSources = (next: ReadonlyArray<WorkflowIntakeSource>) => onChange({ ...node, config: { ...node.config, sources: [...next] } });
  return (
    <div className="space-y-2" data-workflow-intake-sources="">
      <span className="text-xs font-medium">Sources</span>
      {sources.length === 0 ? <p className="text-[12px] text-muted-foreground">No sources yet.</p> : null}
      <ul className="space-y-2">
        {sources.map((source, index) => (
          <SourceCard
            // oxlint-disable-next-line react/no-array-index-key -- Sources have no ids; their place is stable while editing.
            key={index}
            source={source}
            index={index}
            organizationId={organizationId}
            workflowId={workflowId}
            onChange={(next) => setSources(sources.map((entry, at) => (at === index ? next : entry)))}
            onRemove={() => setSources(sources.filter((_, at) => at !== index))}
          />
        ))}
      </ul>
      {sources.length < WORKFLOW_LIMITS.intakeSources ? (
        <Select
          value=""
          onValueChange={(value) => {
            const kind = WORKFLOW_INTAKE_SOURCE_KINDS.find((entry) => entry === value);
            if (kind) setSources([...sources, newIntakeSource(kind, spaceId)]);
          }}
        >
          <SelectTrigger size="sm" aria-label="Add a source" data-workflow-intake-add-source="">
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <PlusIcon className="size-3.5" />
              Add a source
            </span>
          </SelectTrigger>
          <SelectContent>
            {WORKFLOW_INTAKE_SOURCE_KINDS.map((kind) => (
              <SelectItem key={kind} value={kind}>
                {SOURCE_TITLE[kind]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : null}
      <p className="text-[11px] leading-snug text-muted-foreground">
        Reads as the person the run acts for and passes on the text, each document under its own heading. A search can use{" "}
        {"{{input}}"}. PDFs, Word files and workbooks are read as text; anything that cannot be read is listed at the end.
        Reading {INTAKE_SOURCE_LABEL.sharepoint} and {INTAKE_SOURCE_LABEL.googledrive} uses the organization's connection,
        within the places an admin allowed. Email reads the mailbox of the person the run acts for, with their permission.
      </p>
    </div>
  );
}
